import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, ArrowLeftRight, Download } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useCostingMovementReport } from "~/hooks/costing";
import { exportCsv } from "~/utils/csv";

import {
  OIL_REPORT_QTY_FIELDS,
  PeriodSelector,
  fmtCLP,
  fmtQty,
  oilRowToKg,
  useCosteoPeriod,
  useOilDensities,
} from "./components/CosteoShared";
import {
  DailyMovementDialog,
  type DailyMovementItem,
} from "./components/DailyMovementDialog";

type KindFilter = "all" | "raw_material" | "oil_type";

export function CosteoMovimientoMpPage() {
  const navigate = useNavigate();
  const { periods, period, setPeriod } = useCosteoPeriod();
  const { data: report = [], isLoading } = useCostingMovementReport(period, [
    "raw_material",
    "oil_type",
  ]);
  const { data: densities } = useOilDensities();

  const [kind, setKind] = useState<KindFilter>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<DailyMovementItem | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return report
      .filter((r) => kind === "all" || r.item_kind === kind)
      .filter(
        (r) =>
          !q ||
          (r.item_code ?? "").toLowerCase().includes(q) ||
          (r.item_name ?? "").toLowerCase().includes(q)
      );
  }, [report, kind, search]);

  // Aceites are liters-native in the engine but counted in kg by finance/Softland;
  // present them in kg (quantities + WAC), leaving $ values unchanged.
  const displayRows = useMemo(
    () =>
      rows.map((r) => {
        if (r.item_kind !== "oil_type") return r;
        const density = densities?.[r.oil_type_id ?? ""];
        if (!density) return r;
        return {
          ...oilRowToKg(r, density, OIL_REPORT_QTY_FIELDS),
          item_unit: "KGS",
        };
      }),
    [rows, densities]
  );

  const totals = useMemo(
    () =>
      displayRows.reduce(
        (t, r) => ({
          opening: t.opening + Number(r.opening_value),
          purchases: t.purchases + Number(r.purchases_value),
          availability: t.availability + Number(r.availability_value),
          consumption: t.consumption + Number(r.consumption_value),
          adjustments:
            t.adjustments +
            Number(r.adjustments_in_value) -
            Number(r.adjustments_out_value) -
            Number(r.waste_value) -
            Number(r.revaluation_value),
          closing: t.closing + Number(r.closing_value),
        }),
        {
          opening: 0,
          purchases: 0,
          availability: 0,
          consumption: 0,
          adjustments: 0,
          closing: 0,
        }
      ),
    [displayRows]
  );

  const handleExport = () => {
    exportCsv(
      `movimiento_materia_prima_${period}.csv`,
      [
        "Código",
        "Ítem",
        "Unidad",
        "Inv. Inicial (Cant)",
        "Inv. Inicial ($)",
        "Compras (Cant)",
        "Compras ($)",
        "Disponibilidad (Cant)",
        "Disponibilidad ($)",
        "Costo Promedio",
        "Consumos (Cant)",
        "Consumos ($)",
        "Mermas/Ajustes (Cant)",
        "Revalorización ($)",
        "Inv. Final (Cant)",
        "Inv. Final ($)",
      ],
      displayRows.map((r) => [
        r.item_code,
        r.item_name,
        r.item_unit,
        r.opening_qty,
        r.opening_value,
        r.purchases_qty,
        r.purchases_value,
        r.availability_qty,
        r.availability_value,
        r.wac_clp,
        r.consumption_qty,
        r.consumption_value,
        r.waste_qty + r.adjustments_out_qty - r.adjustments_in_qty,
        r.revaluation_value,
        r.closing_qty,
        r.closing_value,
      ])
    );
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/costeo")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ArrowLeftRight className="h-6 w-6" />
            Movimiento de Materia Prima
          </h1>
          <p className="text-muted-foreground">
            Inv. inicial + compras = disponibilidad − consumos = inv. final, al
            costo promedio móvil · haz clic en un ítem para el detalle diario
          </p>
        </div>
        <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={kind} onValueChange={(v) => setKind(v as KindFilter)}>
          <TabsList className="h-9">
            <TabsTrigger value="all">Todos</TabsTrigger>
            <TabsTrigger value="raw_material">Materias Primas</TabsTrigger>
            <TabsTrigger value="oil_type">Aceites</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar código o nombre…"
          className="h-9 max-w-xs"
        />
        <div className="flex-1" />
        <Button variant="outline" className="h-9" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ítem</TableHead>
              <TableHead className="text-right">Inv. Inicial</TableHead>
              <TableHead className="text-right">Compras</TableHead>
              <TableHead className="text-right">Disponibilidad</TableHead>
              <TableHead className="text-right">WAC</TableHead>
              <TableHead className="text-right">Consumos</TableHead>
              <TableHead className="text-right">Mermas/Ajustes</TableHead>
              <TableHead className="text-right">Inv. Final</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : displayRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-muted-foreground"
                >
                  Sin movimientos de materia prima en {period || "este período"}
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((r, index) => {
                const adjQty =
                  Number(r.adjustments_in_qty) -
                  Number(r.adjustments_out_qty) -
                  Number(r.waste_qty);
                const adjValue =
                  Number(r.adjustments_in_value) -
                  Number(r.adjustments_out_value) -
                  Number(r.waste_value) -
                  Number(r.revaluation_value);
                return (
                  <TableRow
                    key={`${r.item_kind}-${r.raw_material_id ?? r.oil_type_id}`}
                  onClick={() =>
                    setSelected({
                      kind: r.item_kind,
                      id: (r.raw_material_id ?? r.oil_type_id) as string,
                      code: r.item_code,
                      name: r.item_name,
                      unit: r.item_unit,
                      density:
                        r.item_kind === "oil_type"
                          ? densities?.[r.oil_type_id ?? ""]
                          : undefined,
                    })
                  }
                  style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  className={`cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50 ${
                    Number(r.closing_qty) < 0 ? "bg-red-50" : ""
                  }`}
                >
                  <TableCell>
                    <div className="font-medium">{r.item_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.item_code} · {r.item_unit}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div>{fmtQty(r.opening_qty)}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtCLP(r.opening_value)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div>{fmtQty(r.purchases_qty)}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtCLP(r.purchases_value)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="font-medium">
                      {fmtQty(r.availability_qty)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fmtCLP(r.availability_value)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {fmtCLP(r.wac_clp)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div>{fmtQty(r.consumption_qty)}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtCLP(r.consumption_value)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {adjQty === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <>
                        <div
                          className={`font-medium ${
                            adjQty < 0 ? "text-red-700" : "text-green-700"
                          }`}
                        >
                          {fmtQty(adjQty)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {fmtCLP(adjValue)}
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div
                      className={`font-medium ${
                        Number(r.closing_qty) < 0 ? "text-destructive" : ""
                      }`}
                    >
                      {fmtQty(r.closing_qty)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fmtCLP(r.closing_value)}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {rows.length > 0 && (
          <div className="grid grid-cols-2 gap-4 border-t bg-muted/30 px-4 py-3 text-sm sm:grid-cols-6">
            <div>
              <div className="text-muted-foreground">Inv. Inicial</div>
              <div className="font-semibold">{fmtCLP(totals.opening)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Compras</div>
              <div className="font-semibold">{fmtCLP(totals.purchases)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Disponibilidad</div>
              <div className="font-semibold">{fmtCLP(totals.availability)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">
                Consumos (→ producción)
              </div>
              <div className="font-semibold">{fmtCLP(totals.consumption)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Mermas/Ajustes</div>
              <div
                className={`font-semibold ${
                  totals.adjustments < 0
                    ? "text-red-700"
                    : totals.adjustments > 0
                      ? "text-green-700"
                      : ""
                }`}
              >
                {fmtCLP(totals.adjustments)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Inv. Final</div>
              <div className="font-semibold">{fmtCLP(totals.closing)}</div>
            </div>
          </div>
        )}
      </div>

      <DailyMovementDialog
        period={period}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </PageContainer>
  );
}
